import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { MapPin, Navigation, Clock, Phone, Check, ChevronDown, Store, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import axios from 'axios';

import { API } from '@/config/api';

export default function BranchSelector({ 
  restaurantId, 
  selectedBranch, 
  onSelectBranch,
  compact = false 
}) {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    // Get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location access denied:', error);
          setLocationError('Location access denied');
        }
      );
    }
  }, []);

  const fetchBranches = useLatestCallback(async () => {
    try {
      let url = `${API}/branches/restaurant/${restaurantId}`;
      if (userLocation) {
        url += `?latitude=${userLocation.latitude}&longitude=${userLocation.longitude}`;
      }
      const response = await axios.get(url);
      setBranches(response.data);
      
      // Auto-select nearest branch if none selected
      if (!selectedBranch && response.data.length > 0) {
        onSelectBranch(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchBranches();
  }, [restaurantId, userLocation, fetchBranches]);

  const handleSelectBranch = (branch) => {
    onSelectBranch(branch);
    setOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading locations...</span>
      </div>
    );
  }

  if (branches.length === 0) {
    return null; // No branches available
  }

  if (branches.length === 1 && !compact) {
    // Only one location, no need for selector
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <MapPin className="h-4 w-4" />
        <span>{branches[0].address}</span>
      </div>
    );
  }

  // Compact mode - just show selected branch with dropdown
  if (compact) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 w-full justify-between">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              <span className="truncate">{selectedBranch?.name || 'Select Location'}</span>
            </div>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {branches.map((branch) => (
              <Card 
                key={branch.id}
                className={`cursor-pointer transition-all ${
                  selectedBranch?.id === branch.id 
                    ? 'ring-2 ring-primary' 
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => handleSelectBranch(branch)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{branch.name}</h4>
                        {selectedBranch?.id === branch.id && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {branch.address}, {branch.city}
                      </p>
                      {branch.distance_miles && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Navigation className="h-3 w-3" />
                          {branch.distance_miles} miles away
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {branch.is_accepting_orders ? (
                        <Badge variant="default" className="bg-green-500">Open</Badge>
                      ) : (
                        <Badge variant="secondary">Closed</Badge>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {branch.delivery_time_min}-{branch.delivery_time_max} min
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Full mode - show all branches
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>{selectedBranch?.name || 'Select a location'}</span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <RadioGroup 
          value={selectedBranch?.id?.toString()} 
          onValueChange={(value) => {
            const branch = branches.find(b => b.id.toString() === value);
            if (branch) handleSelectBranch(branch);
          }}
        >
          <div className="space-y-3">
            {branches.map((branch) => (
              <Label
                key={branch.id}
                htmlFor={`branch-${branch.id}`}
                className="cursor-pointer"
              >
                <Card className={`transition-all ${
                  selectedBranch?.id === branch.id 
                    ? 'ring-2 ring-primary bg-primary/5' 
                    : 'hover:bg-muted/50'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem 
                        value={branch.id.toString()} 
                        id={`branch-${branch.id}`}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{branch.name}</h4>
                          {branch.is_accepting_orders ? (
                            <Badge variant="default" className="bg-green-500">Open</Badge>
                          ) : (
                            <Badge variant="secondary">Closed</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {branch.address}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {branch.city}, {branch.state} {branch.zip_code}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {branch.distance_miles && (
                            <span className="flex items-center gap-1">
                              <Navigation className="h-3 w-3" />
                              {branch.distance_miles} miles
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {branch.delivery_time_min}-{branch.delivery_time_max} min
                          </span>
                          {branch.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {branch.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Label>
            ))}
          </div>
        </RadioGroup>
      </CollapsibleContent>
    </Collapsible>
  );
}
